import { CookiePreferences } from 'common-tracking';
import { useState } from 'react';

import { styled, theme } from '../../stitches.config';
import { Button } from '../button';
import { Column, Columns } from '../layout';
import { ToggleSwitch } from '../switch';
import { P } from '../text';

type Props = {
  savePreferences: (preferences: {
    performance: boolean;
    functional: boolean;
    targeting: boolean;
  }) => void;
  allowAll: () => void;
  initialPreferences?: CookiePreferences;
};
export const DetailedCookiePreferenceBanner = ({
  initialPreferences,
  savePreferences,
  allowAll,
}: Props) => {
  const [functional, setFunctional] = useState(
    initialPreferences?.functional ?? true,
  );
  const [performance, setPerformance] = useState(
    initialPreferences?.performance ?? true,
  );
  const [targeting, setTargeting] = useState(
    initialPreferences?.targeting ?? true,
  );
  return (
    <DialogOverlay>
      <GdprDetails>
        <GdprTitle>Cookie Preferences</GdprTitle>
        <Details>
          Cookies are small piece of data which is stored on a browser, which
          does not usually directly identify you, but it can give you a more
          personalized web experience and make the site work as you expect it
          to. Because we respect your right to privacy, you can choose not to
          allow some types of cookies. However, not allowing some types of
          cookies may affect your experience of the site and the services we are
          able to offer.
        </Details>
        <Columns layout="1-1" css={{ rowGap: '20px' }}>
          <Column>
            <Row>
              <GdprSubTitle>Necessary Cookies</GdprSubTitle>
            </Row>
            <Details>
              These cookies are necessary for the website to function and can´t
              be switched off in our systems. They are usually only set in
              response to actions you have taken which result in a request for
              services, such as setting your privacy preferences, logging in or
              filling in forms, etc.
            </Details>
          </Column>
          <Column>
            <Row>
              <GdprSubTitle>Functional Cookies</GdprSubTitle>
              <ToggleSwitch
                onClick={() => setFunctional(!functional)}
                checked={functional}
              />
            </Row>
            <Details>
              These cookies allow the provision of enhanced functionality and
              personalisation like live chats. They may be set by us or by
              third-party providers whose services we have added to our pages.
              If you don´t allow these cookies, then some or all of these
              functionalities may not function properly.
            </Details>
          </Column>
          <Column>
            <Row>
              <GdprSubTitle>Performance Cookies</GdprSubTitle>
              <ToggleSwitch
                onClick={() => setPerformance(!performance)}
                checked={performance}
              />
            </Row>
            <Details>
              These cookies allow us to anonymously track and measure data so
              that we can measure and improve the performance of our website. If
              you don´t allow these cookies, it will limit our ability to
              improve and give you an exceptional website experience.
            </Details>
          </Column>
          <Column>
            <Row>
              <GdprSubTitle>Targeting Cookies</GdprSubTitle>
              <ToggleSwitch
                onClick={() => setTargeting(!targeting)}
                checked={targeting}
              />
            </Row>
            <Details>
              These cookies are set through our site by our advertising partners
              and show you relevant ads on other sites. They work by uniquely
              identifying your browser and device. If you don´t allow these
              cookies, you will not experience our targeted advertising relevant
              for you across other websites as a result of these cookies.
            </Details>
          </Column>
        </Columns>
        <ActionDiv>
          <Button
            onClick={() =>
              savePreferences({ targeting, functional, performance })
            }
          >
            Save preferences
          </Button>
          <Button color={'secondary'} onClick={allowAll} css={{ width: '16%' }}>
            Allow all
          </Button>
        </ActionDiv>
      </GdprDetails>
    </DialogOverlay>
  );
};

const DialogOverlay = styled('div', {
  boxSizing: 'border-box',
  width: '80%',
  padding: '40px',
  margin: '50px auto',
  borderRadius: '6px',
  bottom: '0px',
  display: 'flex',
  backgroundColor: theme.colors.white,
  boxShadow: '0 0 30px 0 rgb(0 0 0 / 30%)',
  position: 'fixed',
  left: '10%',
  zIndex: '9999',
});

const GdprDetails = styled('div', {
  width: '100%',
  color: theme.colors.gray12,
  textAlign: 'left',
  fontSize: '14px',
  fontWeight: '600',
  letterSpacing: '0',
  lineHeight: '21px',
  display: 'flex',
  gap: 30,
  flexDirection: 'column',
});

const GdprTitle = styled('h2', {
  color: theme.colors.gray12,
  fontSize: '24px',
  margin: 0,
});

const GdprSubTitle = styled('h2', {
  color: theme.colors.gray12,
  fontSize: '20px',
});

const Row = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 15,
});
const ActionDiv = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: 25,
});

const Details = styled(P, {
  fontSize: '14px',
  letterSpacing: '0',
  lineHeight: '21px',
});
